import { useMutation } from "@apollo/client";
import { MaterialIcons } from "@expo/vector-icons";
import {
  LoadingIndicator,
  useFlashMessages,
} from "@towersystems/roam-common-native";
import { NativeRemoveBackgroundBase64Result } from "@towersystems/roam-common/lib/generated-types";
import { CameraCapturedPicture } from "expo-camera";
import { ImageResult, manipulateAsync } from "expo-image-manipulator";
import { ImagePickerAsset } from "expo-image-picker";
import { useEffect, useMemo, useState } from "react";
import { Image, Platform } from "react-native";
import { View } from "react-native-ui-lib";
import { CameraViewFinder } from "../../../../components";
import { checkHasBackgroundRemovalConfigured } from "../../../../utilities/background-eraser";
import {
  getBase64,
  ratioStringToNumber,
  usePrepareRatio,
} from "../../../../utilities/camera/utils";
import { useGlobalSettingsProvider } from "../../../../utilities/global-settings/hooks";
import { UseAwaitReturn } from "../../../../utilities/use-await";
import { makeLoadingState } from "../../../../utilities/use-await/utils";
import { MUTATION_PREVIEW_REMOVE_BACKGROUND_BASE_64 } from "./constants";
import {
  isRemoveBackgroundBase64Result,
  removalResultToErrorMessage,
} from "./utils";

async function cropSquareWeb(uri: string): Promise<ImageResult | null> {
  try {
    const response = await fetch(uri);
    const blob = await response.blob();
    const bitmap = await createImageBitmap(blob);
    const w = bitmap.width;
    const h = bitmap.height;
    if (!w || !h) return null;
    const size = Math.min(w, h);
    const ox = Math.floor((w - size) / 2);
    const oy = Math.floor((h - size) / 2);
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    canvas.getContext("2d")!.drawImage(bitmap, ox, oy, size, size, 0, 0, size, size);
    bitmap.close();
    const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
    return {
      uri: dataUrl,
      width: size,
      height: size,
      base64: dataUrl.replace(/^data:image\/\w+;base64,/, ""),
    };
  } catch {
    return null;
  }
}

async function cropSquareNative(
  uri: string,
  knownDims: { width?: number; height?: number }
): Promise<ImageResult | null> {
  let w = knownDims.width ?? 0;
  let h = knownDims.height ?? 0;
  if (!w || !h) {
    await new Promise<void>((resolve) => {
      Image.getSize(
        uri,
        (pw, ph) => {
          w = pw;
          h = ph;
          resolve();
        },
        () => resolve()
      );
    });
  }
  if (!w || !h) return null;
  const size = Math.min(w, h);
  return manipulateAsync(
    uri,
    [
      {
        crop: {
          originX: Math.floor((w - size) / 2),
          originY: Math.floor((h - size) / 2),
          width: size,
          height: size,
        },
      },
    ],
    { base64: true }
  );
}

export interface CameraPreviewProps {
  photo: CameraCapturedPicture | ImagePickerAsset;
  savePhoto(
    photo: CameraCapturedPicture | ImagePickerAsset,
    removeBg?: boolean
  ): Promise<unknown>;
  retakePicture(): void;
}

export const CameraPreview = ({
  photo,
  savePhoto,
  retakePicture,
}: CameraPreviewProps) => {
  const globalSettings = useGlobalSettingsProvider();

  const { imagePadding, ratio } = usePrepareRatio();

  const { showMessage } = useFlashMessages();
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const [previewRemoveBackgroundBase64] = useMutation<{
    previewRemoveBackgroundBase64: NativeRemoveBackgroundBase64Result;
  }>(MUTATION_PREVIEW_REMOVE_BACKGROUND_BASE_64);

  const [base64NoBackground, setBase64NoBackground] = useState<
    UseAwaitReturn<string> | undefined
  >();
  const [cropResult, setCropResult] = useState<ImageResult | undefined>();
  const [isCropping, setIsCropping] = useState(false);

  const isBackgroundRemovalConfigured =
    checkHasBackgroundRemovalConfigured(globalSettings);

  async function removeBackground() {
    const base64 = getBase64(photo);

    if (!base64) return;

    setBase64NoBackground(makeLoadingState(undefined));

    return previewRemoveBackgroundBase64({
      variables: {
        base64,
      },
    })
      .then(handleRemoveBackgroundResponse)
      .catch((e) => {
        setBase64NoBackground({
          isLoading: false,
          isError: true,
          errorMsg: e?.toString(),
        });
        showMessage({
          message: "Error removing background",
          severity: "error",
        });
      });
  }

  function handleRemoveBackgroundResponse(
    res: Awaited<ReturnType<typeof previewRemoveBackgroundBase64>>
  ) {
    const processedBase64 = res.data?.previewRemoveBackgroundBase64;

    if (!processedBase64) return;

    if (isRemoveBackgroundBase64Result(processedBase64)) {
      setBase64NoBackground({
        isLoading: false,
        isError: false,
        data: processedBase64.base64,
      });
    } else {
      setBase64NoBackground({
        isLoading: false,
        isError: true,
        errorMsg: processedBase64.message,
      });

      showMessage({
        message: removalResultToErrorMessage(processedBase64),
        severity: "error",
      });
    }
  }

  function undoBackgroundRemoval() {
    setBase64NoBackground(undefined);
  }

  async function cropToSquare() {
    const source = cropResult ? cropResult.uri : photo.uri;
    setIsCropping(true);
    try {
      const result =
        Platform.OS === "web"
          ? await cropSquareWeb(source)
          : await cropSquareNative(source, cropResult ?? photo);
      if (!result) {
        showMessage({ message: "Failed to crop image", severity: "error" });
        return;
      }
      setCropResult(result);
      setBase64NoBackground(undefined);
    } catch {
      showMessage({ message: "Failed to crop image", severity: "error" });
    } finally {
      setIsCropping(false);
    }
  }

  function undoCrop() {
    setCropResult(undefined);
  }

  const centerMarkup = useMemo(() => {
    if (base64NoBackground?.data) {
      return (
        <CameraViewFinder.IconButton onPress={undoBackgroundRemoval}>
          <MaterialIcons name="undo" size={24} color="white" />
        </CameraViewFinder.IconButton>
      );
    } else {
      if (base64NoBackground?.isLoading) {
        return (
          <CameraViewFinder.IconButton disabled={true}>
            <LoadingIndicator size="small" color="white" />
          </CameraViewFinder.IconButton>
        );
      } else {
        return (
          <CameraViewFinder.IconButton
            onPress={removeBackground}
            disabled={
              base64NoBackground?.isLoading || !isBackgroundRemovalConfigured
            }
          >
            <MaterialIcons name="content-cut" size={24} color="white" />
          </CameraViewFinder.IconButton>
        );
      }
    }
  }, [base64NoBackground]);

  useEffect(() => {
    setBase64NoBackground(undefined);
    setCropResult(undefined);
  }, [photo]);

  const base64Preamble = "data:image/jpg;base64,";

  const base64Data = base64NoBackground?.data;

  let base64Uri = base64Data;

  if (base64Data && !base64Data.startsWith("data:image")) {
    base64Uri = `${base64Preamble}${base64Data}`;
  }

  // Crop result takes priority over original photo as the base image
  const displayUri = base64Uri || cropResult?.uri || photo.uri;

  return (
    <View
      style={{
        backgroundColor: "black",
        flex: 1,
        width: "100%",
        height: "100%",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {Platform.OS === "web" ? (
        // @ts-ignore
        <img
          src={displayUri}
          style={{
            maxWidth: "100%",
            maxHeight: "80vh",
            objectFit: "contain",
          }}
        />
      ) : (
        <Image
          defaultSource={{ uri: photo.uri }}
          source={{ uri: displayUri }}
          style={{
            width: "100%",
            height: "auto",
            aspectRatio: ratioStringToNumber(ratio),
          }}
        />
      )}
      <View
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          flex: 1,
          flexDirection: "column",
          padding: 15,
          justifyContent: "flex-end",
        }}
      >
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <CameraViewFinder.TextButton
            onPress={retakePicture}
            label="Take Again"
          />

          <View style={{ flexDirection: "row", gap: 8, alignItems: "center" }}>
            <CameraViewFinder.IconButton
              onPress={cropResult ? undoCrop : cropToSquare}
              disabled={isCropping}
            >
              {isCropping ? (
                <LoadingIndicator size="small" color="white" />
              ) : (
                <MaterialIcons
                  name={cropResult ? "crop-free" : "crop-square"}
                  size={24}
                  color="white"
                />
              )}
            </CameraViewFinder.IconButton>
            {centerMarkup}
          </View>

          <CameraViewFinder.TextButton
            disabled={isSaving}
            label={isSaving ? "Saving..." : "Save"}
            onPress={() => {
              setIsSaving(true);
              const effectivePhoto = cropResult
                ? (cropResult as unknown as ImagePickerAsset)
                : photo;
              savePhoto(effectivePhoto, !!base64Data)
                .catch(() => undefined)
                .finally(() => {
                  setIsSaving(false);
                });
            }}
          />
        </View>
      </View>
    </View>
  );
};
